/**
 * App-Level Error Boundary
 * Bulletproof React pattern for global error handling
 * Task: 11.3 - Stage 1: Error Boundary Consolidation
 *
 * This is the last line of defense for unhandled errors in the application.
 * It catches errors that bubble up from any component in the tree and displays
 * a user-friendly fallback UI with recovery options.
 *
 * Features:
 * - Catches all unhandled React errors
 * - Provides user-friendly error messages
 * - Offers retry/reset functionality
 * - Shows detailed error info in development
 * - Integrates with error logging (Sentry)
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorFallback } from './error-fallback';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * App-level Error Boundary Component
 * Must be a class component - React doesn't support error boundaries as function components
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Update state to trigger fallback UI
   * This is called during the "render" phase, so side effects are not permitted
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Log error details for debugging and monitoring
   * This is called during the "commit" phase, so side effects are permitted
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console for development
    console.error('[ErrorBoundary] Unhandled error caught:', {
      error,
      errorInfo,
      componentStack: errorInfo.componentStack,
    });

    // Store error info in state for display
    this.setState({
      errorInfo,
    });

    // Send to error tracking service in production
    if (import.meta.env.PROD && window.Sentry) {
      window.Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
        tags: {
          errorBoundary: 'app-level',
        },
      });
    }

    // Could also send to custom error logging endpoint
    // This would be useful for tracking errors before Sentry is set up
    // or for custom analytics
  }

  /**
   * Reset error boundary state to allow retry
   */
  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Reload the entire page as last resort
   */
  reloadPage = () => {
    window.location.reload();
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <ErrorFallback
          error={error}
          errorInfo={errorInfo}
          resetError={this.resetError}
          reloadPage={this.reloadPage}
        />
      );
    }

    return children;
  }
}
